import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getPaymentStatus, type PaymentStatusResponse } from '../api/paymentApi';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_DURATION_MS = 60000;

type ProcessingState = 'polling' | 'succeeded' | 'failed' | 'timeout';

export function PaymentProcessingPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paymentId = searchParams.get('paymentId');

  const [state, setState] = useState<ProcessingState>('polling');
  const [payment, setPayment] = useState<PaymentStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number>(Date.now());
  const isMountedRef = useRef(true);

  const clearPollTimeout = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const pollPaymentStatus = useCallback(async () => {
    if (!paymentId || !isMountedRef.current) return;

    const elapsed = Date.now() - startTimeRef.current;

    if (elapsed >= MAX_POLL_DURATION_MS) {
      setState('timeout');
      return;
    }

    try {
      const data = await getPaymentStatus(paymentId);

      if (!isMountedRef.current) return;

      setPayment(data);

      switch (data.status) {
        case 'SUCCEEDED':
          setState('succeeded');
          setTimeout(() => {
            if (isMountedRef.current) {
              navigate(`/payment/success?paymentId=${paymentId}`);
            }
          }, 1500);
          break;

        case 'FAILED':
        case 'CANCELLED':
          setState('failed');
          setError(
            data.status === 'FAILED'
              ? 'Your payment could not be processed. Please try again.'
              : 'The payment was cancelled.'
          );
          break;

        case 'PENDING':
        case 'PROCESSING':
        default:
          pollTimeoutRef.current = setTimeout(pollPaymentStatus, POLL_INTERVAL_MS);
          break;
      }
    } catch (err) {
      if (!isMountedRef.current) return;

      console.error('Error polling payment status:', err);
      pollTimeoutRef.current = setTimeout(pollPaymentStatus, POLL_INTERVAL_MS);
    }
  }, [paymentId, navigate]);

  useEffect(() => {
    isMountedRef.current = true;
    startTimeRef.current = Date.now();

    if (!paymentId) {
      setError('No payment ID provided');
      setState('failed');
      return;
    }

    pollPaymentStatus();

    const elapsedInterval = setInterval(() => {
      if (isMountedRef.current) {
        setElapsedSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    return () => {
      isMountedRef.current = false;
      clearPollTimeout();
      clearInterval(elapsedInterval);
    };
  }, [paymentId, pollPaymentStatus, clearPollTimeout]);

  const handleRetry = () => {
    if (payment?.bookingId) {
      navigate(`/payment/${payment.bookingId}`);
    } else {
      navigate('/bookings');
    }
  };

  const handleViewBookings = () => {
    navigate('/bookings');
  };

  if (state === 'polling') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 py-12 px-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
          {/* Processing Animation */}
          <div className="relative w-20 h-20 mx-auto mb-6">
            {/* Outer spinning ring */}
            <div className="absolute inset-0 rounded-full border-4 border-primary-200 dark:border-primary-900" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary-600 dark:border-t-primary-400 animate-spin" />
            {/* Inner pulse */}
            <div className="absolute inset-4 rounded-full bg-primary-100 dark:bg-primary-900/50 animate-pulse flex items-center justify-center">
              <svg
                className="w-6 h-6 text-primary-600 dark:text-primary-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Processing your payment...
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            Please wait while we confirm your payment. This may take a moment.
          </p>

          {/* Progress indicator */}
          <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 mb-4 overflow-hidden">
            <div
              className="bg-primary-600 dark:bg-primary-500 h-2 rounded-full transition-all duration-1000 ease-linear"
              style={{ width: `${Math.min((elapsedSeconds / 60) * 100, 100)}%` }}
            />
          </div>

          <p className="text-sm text-gray-500 dark:text-slate-500">
            {elapsedSeconds < 60
              ? `Checking payment status... (${elapsedSeconds}s)`
              : 'Almost there...'}
          </p>

          {/* Additional info for 3D Secure */}
          {elapsedSeconds > 10 && (
            <div className="mt-6 p-4 bg-primary-50/50 dark:bg-primary-900/20 border border-primary-200/50 dark:border-primary-800/50 rounded-xl text-left">
              <p className="text-sm text-primary-800 dark:text-primary-300">
                <span className="font-semibold">Note:</span> If you were redirected for 3D Secure
                verification, please complete that process in the other window or tab.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (state === 'succeeded') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 py-12 px-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
          {/* Success Icon with animation */}
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center animate-bounce">
            <svg
              className="w-8 h-8 text-success-600 dark:text-success-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Payment Confirmed!
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-4">Redirecting to your receipt...</p>

          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary-600 dark:border-primary-400 border-t-transparent" />
          </div>
        </div>
      </div>
    );
  }

  if (state === 'timeout') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 py-12 px-4">
        <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
          {/* Timeout Icon */}
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-warning-600 dark:text-warning-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            Taking Longer Than Expected
          </h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">
            Your payment is still being processed. This can happen with certain payment methods or
            during high traffic periods.
          </p>

          <div className="p-4 bg-primary-50/50 dark:bg-primary-900/20 border border-primary-200/50 dark:border-primary-800/50 rounded-xl text-left mb-6">
            <p className="text-sm text-primary-800 dark:text-primary-300">
              <span className="font-semibold">What happens next?</span>
              <br />
              You will receive an email confirmation once the payment is complete. You can also check
              your bookings page for the latest status.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleViewBookings}
              className="w-full py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors"
            >
              Check My Bookings
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full py-3 border border-primary-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700/50 text-gray-700 dark:text-slate-200 rounded-xl font-semibold hover:bg-white/70 dark:hover:bg-slate-600/50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors"
            >
              Continue Waiting
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Failed state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-100 via-primary-50 to-secondary-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 py-12 px-4">
      <div className="max-w-md w-full backdrop-blur-lg bg-white/80 dark:bg-slate-800/80 rounded-2xl shadow-xl border border-white/20 dark:border-slate-700/50 p-8 text-center">
        {/* Error Icon */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-error-100 dark:bg-error-900/30 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-error-600 dark:text-error-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">Payment Failed</h1>
        <p className="text-gray-600 dark:text-slate-400 mb-6">
          {error || 'There was a problem processing your payment.'}
        </p>

        {/* Payment details if available */}
        {payment && (
          <div className="bg-gray-50/50 dark:bg-slate-700/50 border border-gray-200/50 dark:border-slate-600/50 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between py-2">
              <span className="text-gray-500 dark:text-slate-400">Amount</span>
              <span className="font-semibold text-gray-900 dark:text-slate-100">
                {payment.amount.toFixed(2)} {payment.currency.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-500 dark:text-slate-400">Status</span>
              <span className="font-semibold text-error-600 dark:text-error-400">{payment.status}</span>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleRetry}
            className="w-full py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={handleViewBookings}
            className="w-full py-3 border border-primary-200 dark:border-slate-600 bg-white/50 dark:bg-slate-700/50 text-gray-700 dark:text-slate-200 rounded-xl font-semibold hover:bg-white/70 dark:hover:bg-slate-600/50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 transition-colors"
          >
            View Bookings
          </button>
        </div>

        <p className="mt-6 text-sm text-gray-500 dark:text-slate-500">
          If the problem persists, please contact support.
        </p>
      </div>
    </div>
  );
}
